import { Link, Skeleton, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { Fragment } from 'react'
import { Button, Divider, IconButton, SkeletonText } from 'utoniq-ui'

import { Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { PreSaleItem } from '@/components/organisms'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  totalPayment: number
  billingStatements: {
    label: Maybe<string> | undefined
    value: string
  }[]
  brand: string
  cardNumber?: string
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
}

export type Action = {
  onPurchase: () => Promise<number | void>
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

/**
 * クレカ自動引き落としに失敗した抽選応募の支払いページ
 * https://zpl.io/0PeLGvQ
 */
export const LotteryApplicationDetailPaymentConfirmationTemplate: Template<
  State,
  Action
> = ({
  state,
  onPurchase,
  onCloseRequire3DSecureModal,
  onCloseExpiredCardModal,
  onClosePaymentErrorModal,
}) => {
  const { t } = useTranslation('lotteryApplicationDetailPaymentConfirmation')
  const { routerPages, router, generatePath } =
    useRouter<{ preSaleId: string }>()
  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t('title'),
        defaultBackPage: generatePath(routerPages.LotteryApplicationDetail, {
          preSaleId: router.query.preSaleId ?? '#',
        }),
        right: <IconButton.Help edge="end" color="primary" />,
      }}
    >
      {state.loading ? (
        <PaymentConfirmationSkeleton />
      ) : (
        <>
          {/* 上部支払いボタン */}
          <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
            <Button
              size="large"
              sx={{ width: 212 }}
              disabled={state.loading}
              loading={state.submitting}
              onClick={onPurchase}
            >
              {t('submit')}
            </Button>
          </Stack>
          <Typography variant="h4" sx={{ py: 5 }}>
            {t('paymentConfirmation')}
          </Typography>
          {/* 支払い情報、総額 */}
          <ConfirmationContentCell
            billingStatements={state.billingStatements}
            totalPayment={state.totalPayment}
            cardNumber={state.cardNumber}
            brand={state.brand}
          />
          <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
            {/* 下部支払いボタン */}
            <Button
              size="large"
              sx={{ width: 212 }}
              disabled={state.loading}
              loading={state.submitting}
              onClick={onPurchase}
            >
              {t('submit')}
            </Button>
            {/* 戻るボタン */}
            <Button
              variant="text"
              sx={{ width: 212 }}
              onClick={() =>
                router.goBack(
                  generatePath(routerPages.LotteryApplicationDetail, {
                    preSaleId: router.query.preSaleId ?? '#',
                  })
                )
              }
            >
              {t('back')}
            </Button>
          </Stack>
        </>
      )}
      <Require3DSecureModal
        open={state.openRequire3DSecureModal}
        onClose={onCloseRequire3DSecureModal}
      />

      <ExpiredCardModal
        open={state.openExpiredCardModal}
        onClose={onCloseExpiredCardModal}
      />

      <PaymentErrorModal
        open={state.openPaymentErrorModal}
        onClose={onClosePaymentErrorModal}
      />
    </BaseLayout>
  )
}

const PaymentConfirmationSkeleton: Cell = () => (
  <CellFrame sx={{ pt: 7, px: 5, pb: 5 }}>
    <Stack alignItems="center">
      <SkeletonText variant="h6" width="60%" />
    </Stack>
    <CellFrame sx={{ pb: 6 }}>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={2} lastWidth="25%" />
      </Stack>
      <Stack sx={{ pt: 6 }}>
        <SkeletonText variant="body2" numberOfLine={5} />
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={6} />
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={6} />
      </Stack>
    </CellFrame>
  </CellFrame>
)

type ConfirmationContentCellProps = {
  totalPayment: number
  billingStatements: {
    label: Maybe<string> | undefined
    value: string
  }[]
  brand: State['brand']
  cardNumber: State['cardNumber']
}
const ConfirmationContentCell: Cell<ConfirmationContentCellProps> = ({
  billingStatements,
  totalPayment,
  brand,
  cardNumber,
}) => {
  const { t } = useTranslation('lotteryApplicationDetailPaymentConfirmation')
  const { routerPages } = useRouter()

  return (
    <CellFrame sx={{ py: 5 }}>
      {/* 支払情報 */}
      <PreSaleItem title={t('paymentInfo')}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            {t('creditCardInfo')}
          </Typography>
          {!cardNumber && (
            <Link variant="subtitle1" href={routerPages.PaymentMethodSetting}>
              {t('register')}
            </Link>
          )}
        </Stack>
        {!cardNumber ? (
          <Typography variant="caption" color="text.hint">
            {t('cardInfoNotRegister')}
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={3}>
            <StorageImage
              width={40}
              height={32}
              src={generateCreditCardLogoPath(brand)}
              target="common"
            />
            <Typography variant="body2">
              {t('last4Digit', { number: cardNumber })}
            </Typography>
          </Stack>
        )}
      </PreSaleItem>
      {/* お支払い総額 */}
      <PreSaleItem title={t('totalPayment')}>
        <Stack spacing={3}>
          <Stack>
            {billingStatements.map(({ value, label }, index) => (
              <Fragment key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
                <Divider />
              </Fragment>
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ py: 2 }}
            >
              <Typography variant="subtitle1" color="text.secondary">
                {t('total')}
              </Typography>
              <Typography variant="h6">
                {`￥${numeral(totalPayment).format('0,0')}`}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </PreSaleItem>
    </CellFrame>
  )
}
